"use server"

import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import { HttpTypes } from "@medusajs/types"
import { FetchError } from "@medusajs/js-sdk"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import { safeNext } from "@lib/util/safe-next"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  getPendingCustomer,
  removeAuthToken,
  removeCartId,
  removePendingCustomer,
  setAuthToken,
  setPendingCustomer,
} from "./cookies"

export type CustomerAuthState =
  | { state: "error"; error: string }
  | { state: "verification_required"; email: string }
  | { state: "success" }
  | null

// Requests a verification email for the given customer. The request must be
// authenticated with a token tied to the auth identity (the token returned by
// register or by a login that requires verification).
async function requestVerificationEmail(email: string, token: string) {
  await sdk.auth.verification.request(
    {
      entity_id: email,
      entity_type: "email",
    },
    {
      authorization: `Bearer ${token}`,
    }
  )
}

export const retrieveCustomer =
  async (): Promise<HttpTypes.StoreCustomer | null> => {
    const authHeaders = await getAuthHeaders()

    if (!authHeaders) return null

    const headers = {
      ...authHeaders,
    }

    const next = {
      ...(await getCacheOptions("customers")),
    }

    return await sdk.client
      .fetch<{ customer: HttpTypes.StoreCustomer }>(`/store/customers/me`, {
        method: "GET",
        query: {
          fields: "*orders",
        },
        headers,
        next,
        cache: "force-cache",
      })
      .then(({ customer }) => customer)
      .catch(() => null)
  }

export const updateCustomer = async (body: HttpTypes.StoreUpdateCustomer) => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  const updateRes = await sdk.store.customer
    .update(body, {}, headers)
    .then(({ customer }) => customer)
    .catch(medusaError)

  const cacheTag = await getCacheTag("customers")
  revalidateTag(cacheTag)

  return updateRes
}

// Backend auth errors come in English; show the customer what to do instead.
function authErrorMessage(error: unknown): string {
  const message = (error as { message?: string })?.message ?? String(error)
  if (/invalid email or password/i.test(message)) {
    return "El email o la contraseña no son correctos."
  }
  if (/already exists/i.test(message)) {
    return "Ya existe una cuenta con este email. Inicia sesión o recupera tu contraseña."
  }
  if (/fetch failed|network|ECONNREFUSED/i.test(message)) {
    return "No hemos podido conectar con la tienda. Inténtalo de nuevo en unos segundos."
  }
  return "No se ha podido completar la operación. Inténtalo de nuevo."
}

const MIN_PASSWORD_LENGTH = 8

export async function signup(
  _currentState: unknown,
  formData: FormData
): Promise<CustomerAuthState> {
  const password = formData.get("password") as string
  const next = safeNext(formData.get("next"))
  const customerForm = {
    email: String(formData.get("email") ?? "").trim(),
    first_name: formData.get("first_name") as string,
    last_name: formData.get("last_name") as string,
    phone: formData.get("phone") as string,
  }

  if ((password ?? "").length < MIN_PASSWORD_LENGTH) {
    return {
      state: "error",
      error: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
    }
  }

  let identityExisted = false
  try {
    await sdk.auth.register("customer", "emailpass", {
      email: customerForm.email,
      password,
    })
  } catch (error) {
    const fetchError = error as FetchError
    // An existing identity (for example, an admin user with the same email) is
    // expected and handled: the customer can still log in to link a customer
    // record. Any other error is surfaced.
    if (
      fetchError.statusText !== "Unauthorized" ||
      fetchError.message !== "Identity with email already exists"
    ) {
      return { state: "error", error: authErrorMessage(error) }
    }
    identityExisted = true
  }

  // Persist the extra signup fields. The customer record is created during
  // login, which is deferred until after email verification when the backend
  // requires it.
  await setPendingCustomer({ ...customerForm, next: next ?? undefined })

  // Continue by logging in. The login response tells us whether the backend
  // requires email verification — we don't need a storefront-side flag.
  const result = await completeLogin(customerForm.email, password, next)
  if (result?.state === "error" && identityExisted) {
    return {
      state: "error",
      error:
        "Ya existe una cuenta con este email. Inicia sesión o recupera tu contraseña.",
    }
  }
  return result
}

export async function login(
  _currentState: unknown,
  formData: FormData
): Promise<CustomerAuthState> {
  const email = String(formData.get("email") ?? "").trim()
  const password = formData.get("password") as string

  return completeLogin(email, password, safeNext(formData.get("next")))
}

// Logs the customer in and reconciles the customer record. The behavior is
// driven entirely by the backend's login response, so it works whether or not
// email verification is enabled. With `next`, a successful login continues on
// that page (e.g. the checkout the customer came from).
async function completeLogin(
  email: string,
  password: string,
  next?: string | null
): Promise<CustomerAuthState> {
  let result: Awaited<ReturnType<typeof sdk.auth.login>>

  try {
    result = await sdk.auth.login("customer", "emailpass", { email, password })
  } catch (error) {
    return { state: "error", error: authErrorMessage(error) }
  }

  // A `location` is returned by third-party auth providers, which this flow
  // doesn't support.
  if (typeof result === "object" && "location" in result) {
    return {
      state: "error",
      error: "Este método de inicio de sesión no está disponible.",
    }
  }

  // The backend requires email verification and the customer hasn't verified
  // yet. Send the verification email and ask them to check their inbox.
  if (
    typeof result === "object" &&
    "verification_required" in result &&
    result.verification_required
  ) {
    try {
      await requestVerificationEmail(email, result.token)
    } catch {
      // Ignore: the customer can resend from the verification page.
    }
    // Remember where to go back to once the email is verified.
    if (next) {
      const pending = await getPendingCustomer()
      await setPendingCustomer({ ...(pending ?? { email }), next })
    }
    return { state: "verification_required", email }
  }

  if (typeof result !== "string") {
    return {
      state: "error",
      error: "El inicio de sesión requiere pasos adicionales que no están disponibles.",
    }
  }

  let token = result

  // The token may not be tied to a customer record yet — right after
  // registration, or after verifying a brand-new account. Ask the backend:
  // `/store/customers/me` rejects tokens without a registered actor, so a
  // failed retrieve means we still need to create the customer, then log in
  // again to obtain a customer-bound token.
  const customerExists = await sdk.store.customer
    .retrieve({}, { authorization: `Bearer ${token}` })
    .then(() => true)
    .catch(() => false)

  if (!customerExists) {
    const pending = await getPendingCustomer()

    try {
      await sdk.store.customer.create(
        {
          email,
          first_name: pending?.first_name,
          last_name: pending?.last_name,
          phone: pending?.phone || undefined,
        },
        {},
        { authorization: `Bearer ${token}` }
      )

      token = (await sdk.auth.login("customer", "emailpass", {
        email,
        password,
      })) as string
    } catch (error) {
      return { state: "error", error: authErrorMessage(error) }
    }

    await removePendingCustomer()
  }

  await setAuthToken(token)

  const customerCacheTag = await getCacheTag("customers")
  revalidateTag(customerCacheTag)

  try {
    await transferCart()
  } catch {
    // The basket stays as a guest cart; the one-page checkout transfers it.
  }

  if (next) {
    redirect(next)
  }

  return { state: "success" }
}

// Confirms a customer's email using the token from the verification link.
//
// The confirm route doesn't require authentication, so this works even when the
// customer opens the link on a different device than the one they signed up on.
//
// When the link is opened in the browser used to sign up, the email and the
// page to return to are read back so the customer only types the password.
export async function confirmEmailVerification(token: string): Promise<{
  success: boolean
  error?: string
  email?: string
  next?: string
}> {
  const pending = await getPendingCustomer()
  const remembered = {
    email: pending?.email,
    next: safeNext(pending?.next) ?? undefined,
  }
  try {
    await sdk.auth.verification.confirm({ code: token })
    return { success: true, ...remembered }
  } catch (error) {
    return { success: false, error: String(error), ...remembered }
  }
}

export async function signout(countryCode: string) {
  await sdk.auth.logout()

  await removeAuthToken()

  const customerCacheTag = await getCacheTag("customers")
  revalidateTag(customerCacheTag)

  await removeCartId()

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  redirect(`/${countryCode}/account`)
}

export async function transferCart() {
  const cartId = await getCartId()

  if (!cartId) {
    return
  }

  const headers = await getAuthHeaders()

  await sdk.store.cart.transferCart(cartId, {}, headers)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)
}

// Fields of the address book forms (account and checkout).
function addressFromForm(formData: FormData) {
  const text = (name: string) => String(formData.get(name) ?? "").trim()
  return {
    first_name: text("first_name"),
    last_name: text("last_name"),
    company: text("company"),
    address_1: text("address_1"),
    address_2: text("address_2"),
    city: text("city"),
    postal_code: text("postal_code"),
    province: text("province"),
    country_code: text("country_code").toLowerCase(),
    phone: text("phone"),
    is_default_shipping: formData.get("is_default_shipping") === "on",
    is_default_billing: formData.get("is_default_billing") === "on",
  }
}

const addressError = (address: ReturnType<typeof addressFromForm>) =>
  address.phone
    ? null
    : "Añade un teléfono: el transportista lo necesita para la entrega."

export const addCustomerAddress = async (
  _currentState: Record<string, unknown>,
  formData: FormData
): Promise<{ success: boolean; error: string | null }> => {
  const address = addressFromForm(formData)
  const error = addressError(address)
  if (error) {
    return { success: false, error }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .createAddress(address, {}, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch(() => {
      return {
        success: false,
        error: "No se ha podido guardar la dirección. Inténtalo de nuevo.",
      }
    })
}

export const deleteCustomerAddress = async (
  addressId: string
): Promise<void> => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.customer
    .deleteAddress(addressId, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch((err) => {
      return { success: false, error: err.toString() }
    })
}

export const updateCustomerAddress = async (
  currentState: Record<string, unknown>,
  formData: FormData
): Promise<{ success: boolean; error: string | null }> => {
  const addressId =
    (currentState.addressId as string) || (formData.get("addressId") as string)

  if (!addressId) {
    return { success: false, error: "Falta el identificador de la dirección." }
  }

  const address = addressFromForm(formData)
  const error = addressError(address)
  if (error) {
    return { success: false, error }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .updateAddress(addressId, address, {}, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch(() => {
      return {
        success: false,
        error: "No se ha podido guardar la dirección. Inténtalo de nuevo.",
      }
    })
}

/**
 * Sends the password reset email. It always reports success so the form does
 * not reveal which emails have an account.
 */
export async function requestPasswordReset(_state: unknown, formData: FormData) {
  const email = String(formData.get("email") ?? "").trim()
  if (email) {
    await sdk.auth
      .resetPassword("customer", "emailpass", { identifier: email })
      .catch(() => undefined)
  }
  return { sent: true, email }
}

/** Sets a new password with the token received by email. */
export async function resetPassword(_state: unknown, formData: FormData) {
  const token = String(formData.get("token") ?? "")
  const email = String(formData.get("email") ?? "")
  const password = String(formData.get("password") ?? "")
  if (password.length < 8) {
    return {
      ok: false,
      error: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
    }
  }
  if (password !== String(formData.get("confirm") ?? "")) {
    return { ok: false, error: "Las contraseñas no coinciden." }
  }
  const updated = await sdk.auth
    .updateProvider("customer", "emailpass", { email, password }, token)
    .then(() => true)
    .catch(() => false)
  if (!updated) {
    return {
      ok: false,
      error: "El enlace no es válido o ha caducado. Solicita uno nuevo.",
    }
  }
  // Sign in with the new password so the customer does not have to type it again.
  const login = email
    ? await completeLogin(email, password)
    : ({ state: "error", error: "" } as const)
  return { ok: true, error: null, loggedIn: login?.state === "success" }
}

/**
 * "Guarda tus datos" on the order confirmation page: creates the account with
 * the email, name and address of the guest order just placed, signs in and
 * moves that order into the new account.
 */
export async function createAccountFromOrder(
  _state: unknown,
  formData: FormData
): Promise<CustomerAuthState> {
  const orderId = String(formData.get("order_id") ?? "")
  const password = String(formData.get("password") ?? "")
  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      state: "error",
      error: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
    }
  }

  const order = await sdk.store.order
    .retrieve(orderId, { fields: "id,email,*shipping_address,*billing_address" })
    .then(({ order }) => order)
    .catch(() => null)
  if (!order?.email) {
    return { state: "error", error: "No hemos encontrado el pedido." }
  }
  const email = order.email
  const shipping = order.shipping_address

  try {
    await sdk.auth.register("customer", "emailpass", { email, password })
  } catch (error) {
    if (/already exists/i.test((error as FetchError)?.message ?? "")) {
      return {
        state: "error",
        error:
          "Ya tienes una cuenta con este email. Inicia sesión para ver el pedido en tu cuenta.",
      }
    }
    return { state: "error", error: authErrorMessage(error) }
  }

  await setPendingCustomer({
    email,
    first_name: shipping?.first_name ?? undefined,
    last_name: shipping?.last_name ?? undefined,
    phone: shipping?.phone ?? undefined,
  })
  const result = await completeLogin(email, password)
  if (result?.state !== "success") {
    return result
  }

  const headers = { ...(await getAuthHeaders()) }
  // Keep the order in the account and its addresses in the address book.
  await sdk.client
    .fetch("/store/customers/me/claim-order", {
      method: "POST",
      body: { order_id: order.id },
      headers,
    })
    .catch(() => undefined)

  const toBook = (a: HttpTypes.StoreOrderAddress) => ({
    first_name: a.first_name ?? undefined,
    last_name: a.last_name ?? undefined,
    company: a.company ?? undefined,
    address_1: a.address_1 ?? undefined,
    address_2: a.address_2 ?? undefined,
    city: a.city ?? undefined,
    postal_code: a.postal_code ?? undefined,
    province: a.province ?? undefined,
    country_code: a.country_code ?? undefined,
    phone: a.phone ?? undefined,
  })
  const billing = order.billing_address
  const billingDiffers =
    !!billing?.address_1 &&
    (billing.address_1 !== shipping?.address_1 ||
      billing.postal_code !== shipping?.postal_code)
  if (shipping?.address_1) {
    await sdk.store.customer
      .createAddress(
        {
          ...toBook(shipping),
          is_default_shipping: true,
          is_default_billing: !billingDiffers,
        },
        {},
        headers
      )
      .catch(() => undefined)
  }
  if (billing && billingDiffers) {
    await sdk.store.customer
      .createAddress({ ...toBook(billing), is_default_billing: true }, {}, headers)
      .catch(() => undefined)
  }

  revalidateTag(await getCacheTag("customers"))
  revalidateTag(await getCacheTag("orders"))
  return { state: "success" }
}
